import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPublicClient, createWalletClient, custom, formatUnits, http, isAddress, pad, parseUnits } from "viem";
import { createRoot } from "react-dom/client";
import { chainByDomain, chainByKey, chains, cctpEta } from "./chains";
import { erc20Abi, FORWARDING_HOOK, tokenMessengerAbi, ZERO_BYTES32 } from "./cctp";
import { discoverWallets, isManuallyDisconnected, markWalletAuthorized, setManualDisconnect, wasWalletAuthorizedByUser, type DiscoveredWallet, type Eip1193Provider } from "./wallet";
import { connectWalletConnect as openWalletConnect, disconnectWalletConnect, walletConnectConfigured } from "./walletconnect";
import { fetchOperation, fetchRecentRecipients, operationUrl, patchOperation, readAddressBook, readApiJson, readOperationHistory, removeOperationFromHistory, saveAddressBookEntry, saveOperationToHistory, type AddressBookEntry, type PublicOperation, type RecentRecipient } from "./operations";
import { payForAndDeliverTestnetGas } from "./gas";
import { baseClientContext, captureClientError, initErrorReporting, track } from "./telemetry";
import { DesignPrototypePage } from "./design";
import "./styles.css";
import "./design.css";

type Finality = "standard" | "fast";
type Theme = "dark" | "light";
type TransferStage = "idle" | "creating" | "gas_payment" | "gas_delivery" | "approval" | "burn_sign" | "burn_submitted";

type Quote = {
  finality: Finality;
  finalityThreshold: number;
  transferAmount: string;
  forwardingFee: string;
  protocolFee: string;
  maxFee: string;
  totalBurn: string;
  gasCharge: string;
  grandTotal: string;
  gas?: {
    enabled: boolean;
    treasuryAddress: `0x${string}` | null;
    saleUsd: string;
    paymentUsdc: string;
    paymentUsdcRaw: string;
    nativeSymbol: string;
    nativeAmount: string;
    nativeAmountWei: string;
    referenceEthUsd: string;
    markupBps: number;
    priceSource: string;
    priceUpdatedAt: string;
    nativeMarketValueUsd: string;
    serviceFeeUsd: string;
  } | null;
  display: { receive: string; forwardingFee: string; protocolFee: string; totalFee: string; totalBurn: string; gasCharge: string; grandTotal: string };
};

type Status = { status: string; forwardTxHash?: string | null };

type SavedTransfer = {
  sourceKey: string;
  destinationKey: string;
  sourceDomain: number;
  burnHash: `0x${string}`;
  operationId?: string | null;
  at: number;
};

function short(value?: string) {
  return value ? `${value.slice(0, 6)}…${value.slice(-4)}` : "";
}

function userRejected(error: unknown) {
  const candidate = error as { code?: number; cause?: { code?: number }; message?: string; shortMessage?: string };
  return candidate?.code === 4001 || candidate?.cause?.code === 4001 || /reject|denied|cancelled/i.test(`${candidate?.shortMessage ?? ""} ${candidate?.message ?? ""}`);
}

function friendlyError(error: unknown, fallback: string) {
  const text = error instanceof Error ? error.message : String(error ?? "");
  if (/string did not match the expected pattern/i.test(text)) return fallback;
  if (/failed to fetch|networkerror|load failed/i.test(text)) return "Network request failed. Please retry.";
  return text || fallback;
}

function initialTheme(): Theme {
  try {
    return localStorage.getItem("scc:theme") === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

function parseUsdcSafe(value: string | null) {
  if (!value) return null;
  try {
    return parseUnits(value, 6);
  } catch {
    return null;
  }
}

function App() {
  const [sourceKey, setSourceKey] = useState("base-sepolia");
  const [destinationKey, setDestinationKey] = useState("arbitrum-sepolia");
  const [amount, setAmount] = useState("1");
  const [finality, setFinality] = useState<Finality>("standard");
  const [quotes, setQuotes] = useState<Partial<Record<Finality, Quote>>>({});
  const [quoteErrors, setQuoteErrors] = useState<Partial<Record<Finality, string>>>({});
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [wallets, setWallets] = useState<DiscoveredWallet[]>([]);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [provider, setProvider] = useState<Eip1193Provider | null>(null);
  const [walletName, setWalletName] = useState("");
  const [walletTransport, setWalletTransport] = useState<"injected" | "walletconnect" | "">("");
  const [account, setAccount] = useState<`0x${string}` | null>(null);
  const [customRecipient, setCustomRecipient] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [recipientTouched, setRecipientTouched] = useState(false);
  const [balance, setBalance] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("Ready");
  const [flowStage, setFlowStage] = useState<TransferStage>("idle");
  const [transferError, setTransferError] = useState("");
  const [burnHash, setBurnHash] = useState<`0x${string}` | null>(null);
  const [mintHash, setMintHash] = useState<string | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [gasTopupEnabled, setGasTopupEnabled] = useState(false);
  const [gasTopupUsd, setGasTopupUsd] = useState("0.50");
  const [customGasTopup, setCustomGasTopup] = useState("");
  const [recentRecipients, setRecentRecipients] = useState<RecentRecipient[]>([]);
  const [addressBook, setAddressBook] = useState<AddressBookEntry[]>(() => readAddressBook());
  const [addressBookLabel, setAddressBookLabel] = useState("");
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const lastTrackedStatus = useRef("");
  const silentAttempted = useRef(new Set<string>());

  const source = useMemo(() => chainByKey(sourceKey), [sourceKey]);
  const destination = useMemo(() => chainByKey(destinationKey), [destinationKey]);
  const quote = quotes[finality] ?? null;
  const effectiveRecipient = customRecipient ? recipient.trim() : account ?? "";
  const recipientError = customRecipient && recipientTouched && !isAddress(recipient.trim()) ? "Enter a valid EVM address (0x + 40 hex characters)." : "";
  const balanceRaw = useMemo(() => parseUsdcSafe(balance), [balance]);
  const requiredRaw = quote ? BigInt(quote.grandTotal) : null;
  const insufficientBalance = Boolean(account && balanceRaw != null && requiredRaw != null && balanceRaw < requiredRaw);
  const selectedGasTopupUsd = gasTopupEnabled ? (gasTopupUsd === "custom" ? customGasTopup.trim() : gasTopupUsd) : null;
  const gasPreviewValid = !gasTopupEnabled || Boolean(selectedGasTopupUsd && Number(selectedGasTopupUsd) >= 0.01 && Number(selectedGasTopupUsd) <= 5);
  const preferredWalletId = useMemo(() => {
    try { return localStorage.getItem("scc:preferredWallet") ?? ""; } catch { return ""; }
  }, [wallets, account]);
  const sortedWallets = useMemo(() => (
    [...wallets].sort((a, b) => Number(b.id === preferredWalletId) - Number(a.id === preferredWalletId))
  ), [wallets, preferredWalletId]);

  const clientContext = (overrides: Record<string, string | undefined> = {}) => ({
    ...baseClientContext,
    walletName: walletName || undefined,
    walletTransport: walletTransport || undefined,
    sourceChain: source.name,
    destinationChain: destination.name,
    ...overrides
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("scc:theme", theme); } catch { /* storage may be restricted in some WebViews */ }
  }, [theme]);

  useEffect(() => {
    if (!walletModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") setWalletModalOpen(false); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [walletModalOpen]);

  useEffect(() => {
    void initErrorReporting();
    try {
      if (!sessionStorage.getItem("scc:appOpenTracked")) {
        sessionStorage.setItem("scc:appOpenTracked", "1");
        void track("APP_OPEN", clientContext());
      }
    } catch {
      void track("APP_OPEN", clientContext());
    }

    try {
      const raw = localStorage.getItem("scc:lastTransfer");
      if (!raw) return;
      const saved = JSON.parse(raw) as SavedTransfer;
      if (Date.now() - saved.at > 7 * 24 * 60 * 60 * 1000) return;
      if (!chains.some((chain) => chain.key === saved.sourceKey)) return;
      setSourceKey(saved.sourceKey);
      setDestinationKey(saved.destinationKey);
      setOperationId(saved.operationId ?? null);
      if (saved.operationId) saveOperationToHistory(saved.operationId);
      setBurnHash(saved.burnHash);
      setStep("Resuming transfer tracking");
    } catch {
      try { localStorage.removeItem("scc:lastTransfer"); } catch { /* noop */ }
    }
  }, []);

  useEffect(() => discoverWallets(setWallets), []);

  useEffect(() => {
    if (!customRecipient) return;
    void fetchRecentRecipients().then(setRecentRecipients).catch(() => setRecentRecipients([]));
    setAddressBook(readAddressBook());
  }, [customRecipient, mintHash]);

  // Silent reconnect is only allowed after FreshMint has seen an explicit Connect action for this provider.
  useEffect(() => {
    if (account || provider || isManuallyDisconnected() || wallets.length === 0) return;
    const ordered = [...wallets].sort((a, b) => Number(b.id === preferredWalletId) - Number(a.id === preferredWalletId));
    let cancelled = false;

    const reconnect = async () => {
      for (const wallet of ordered) {
        if (!wasWalletAuthorizedByUser(wallet.id, preferredWalletId)) continue;
        if (silentAttempted.current.has(wallet.id)) continue;
        silentAttempted.current.add(wallet.id);
        try {
          const addresses = (await wallet.provider.request({ method: "eth_accounts" })) as string[];
          const address = addresses?.[0];
          if (cancelled || !address || !isAddress(address)) continue;
          setProvider(wallet.provider);
          setWalletName(wallet.name);
          setWalletTransport("injected");
          setAccount(address);
          setStep("Wallet connected");
          void track("WALLET_RECONNECTED", clientContext({ walletName: wallet.name, walletTransport: "injected" }));
          break;
        } catch {
          // Silent reconnect failure is intentionally non-blocking.
        }
      }
    };
    void reconnect();
    return () => { cancelled = true; };
  }, [wallets, account, provider, preferredWalletId]);

  useEffect(() => {
    if (!provider) return;
    const onAccountsChanged = (...args: unknown[]) => {
      const addresses = args[0] as string[] | undefined;
      const next = addresses?.[0];
      if (next && isAddress(next)) {
        setAccount(next);
        setBalance(null);
        setStep("Wallet connected");
        setTransferError("");
        void track("WALLET_ACCOUNT_CHANGED", clientContext());
      } else {
        setProvider(null);
        setAccount(null);
        setWalletName("");
        setWalletTransport("");
        setBalance(null);
        setStep("Ready");
      }
    };
    provider.on?.("accountsChanged", onAccountsChanged);
    return () => provider.removeListener?.("accountsChanged", onAccountsChanged);
  }, [provider]);

  useEffect(() => {
    if (sourceKey === destinationKey) {
      const alternate = chains.find((c) => c.key !== sourceKey);
      if (alternate) setDestinationKey(alternate.key);
    }
  }, [sourceKey, destinationKey]);

  async function fetchQuote(which: Finality): Promise<Quote> {
    const response = await fetch("/api/cctp/quote", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sourceDomain: source.domain, destinationDomain: destination.domain, amount, finality: which, gasTopupUsd: selectedGasTopupUsd })
    });
    return readApiJson<Quote>(response, "Quote temporarily unavailable. Please retry.");
  }

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      setQuotes({});
      setQuoteErrors({});
      const numericAmount = Number(amount);
      if (!amount || !Number.isFinite(numericAmount) || numericAmount <= 0 || source.domain === destination.domain) return;
      setQuotesLoading(true);
      const results = await Promise.allSettled((["standard", "fast"] as Finality[]).map(async (which) => {
        const context = clientContext();
        void track("QUOTE_REQUESTED", context, { amount, finality: which });
        const started = performance.now();
        try {
          const data = await fetchQuote(which);
          void track("QUOTE_RECEIVED", context, {
            amount,
            finality: which,
            durationMs: Math.round(performance.now() - started),
            forwardingFee: data.display.forwardingFee,
            protocolFee: data.display.protocolFee
          });
          return { which, data };
        } catch (error) {
          void track("QUOTE_FAILED", context, { finality: which, durationMs: Math.round(performance.now() - started) });
          void captureClientError(error, { stage: "quote", finality: which, source: source.name, destination: destination.name });
          throw { which, error };
        }
      }));

      const nextQuotes: Partial<Record<Finality, Quote>> = {};
      const nextErrors: Partial<Record<Finality, string>> = {};
      for (const result of results) {
        if (result.status === "fulfilled") nextQuotes[result.value.which] = result.value.data;
        else {
          const failure = result.reason as { which?: Finality; error?: unknown };
          if (failure.which) nextErrors[failure.which] = friendlyError(failure.error, "Live fee unavailable. Please retry.");
        }
      }
      setQuotes(nextQuotes);
      setQuoteErrors(nextErrors);
      setQuotesLoading(false);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [amount, source.domain, destination.domain, selectedGasTopupUsd]);

  useEffect(() => {
    if (!account) {
      setBalance(null);
      return;
    }
    void refreshBalance();
  }, [account, sourceKey]);

  useEffect(() => {
    if (!burnHash) return;
    let stopped = false;
    lastTrackedStatus.current = "";
    const poll = async () => {
      while (!stopped) {
        try {
          const response = await fetch(`/api/cctp/status/${source.domain}/${burnHash}`);
          const data = await readApiJson<Status>(response, "Transfer status temporarily unavailable.");
          if (data.status !== lastTrackedStatus.current) {
            lastTrackedStatus.current = data.status;
            if (data.status === "attested") {
              void patchOperation(operationId, { status: "ATTESTED" });
              void track("CCTP_ATTESTED", clientContext(), undefined, operationId);
            }
          }
          if (data.forwardTxHash) {
            setMintHash(data.forwardTxHash);
            setStep("Fresh mint complete");
            setTransferError("");
            localStorage.removeItem("scc:lastTransfer");
            void patchOperation(operationId, { status: "COMPLETED", destinationTxHash: data.forwardTxHash });
            void track("DESTINATION_MINT_CONFIRMED", clientContext(), { destinationTxHash: data.forwardTxHash }, operationId);
            void track("OPERATION_COMPLETED", clientContext(), undefined, operationId);
            return;
          }
          setStep(data.status === "attested" ? "Attested · forwarding mint" : "Waiting for Circle attestation");
        } catch (error) {
          void captureClientError(error, { stage: "status_poll", operationId: operationId ?? undefined });
        }
        await new Promise((resolve) => setTimeout(resolve, 2500));
      }
    };
    void poll();
    return () => { stopped = true; };
  }, [burnHash, source.domain, operationId]);

  async function connect(wallet: DiscoveredWallet) {
    void track("WALLET_CONNECT_STARTED", clientContext({ walletName: wallet.name, walletTransport: "injected" }));
    setTransferError("");
    try {
      const addresses = (await wallet.provider.request({ method: "eth_requestAccounts" })) as string[];
      const address = addresses[0];
      if (!address || !isAddress(address)) throw new Error("Wallet returned an invalid address");
      markWalletAuthorized(wallet.id);
      try { localStorage.setItem("scc:preferredWallet", wallet.id); } catch { /* noop */ }
      setProvider(wallet.provider);
      setWalletName(wallet.name);
      setWalletTransport("injected");
      setAccount(address);
      setWalletModalOpen(false);
      setStep("Wallet connected");
      void track("WALLET_CONNECTED", clientContext({ walletName: wallet.name, walletTransport: "injected" }));
    } catch (error) {
      const message = userRejected(error) ? "Connection cancelled in wallet." : friendlyError(error, "Could not connect wallet.");
      setTransferError(message);
      void track("WALLET_CONNECT_FAILED", clientContext({ walletName: wallet.name, walletTransport: "injected" }));
      void captureClientError(error, { stage: "wallet_connect", wallet: wallet.name });
    }
  }

  async function connectViaWalletConnect() {
    void track("WALLET_CONNECT_STARTED", clientContext({ walletName: "WalletConnect", walletTransport: "walletconnect" }));
    setTransferError("");
    try {
      const wcProvider = await openWalletConnect();
      const addresses = (await wcProvider.request({ method: "eth_accounts" })) as string[];
      const address = addresses?.[0];
      if (!address || !isAddress(address)) throw new Error("WalletConnect returned an invalid address");
      setManualDisconnect(false);
      setProvider(wcProvider);
      setWalletName("WalletConnect");
      setWalletTransport("walletconnect");
      setAccount(address);
      setWalletModalOpen(false);
      setStep("Wallet connected");
      void track("WALLET_CONNECTED", clientContext({ walletName: "WalletConnect", walletTransport: "walletconnect" }));
    } catch (error) {
      const message = userRejected(error) ? "Connection cancelled in wallet." : friendlyError(error, "Could not connect with WalletConnect.");
      setTransferError(message);
      void track("WALLET_CONNECT_FAILED", clientContext({ walletName: "WalletConnect", walletTransport: "walletconnect" }));
      void captureClientError(error, { stage: "walletconnect_connect" });
    }
  }

  async function disconnect() {
    if (walletTransport === "walletconnect") {
      await disconnectWalletConnect(provider);
    } else if (provider) {
      try {
        await provider.request({ method: "wallet_revokePermissions", params: [{ eth_accounts: {} }] });
      } catch {
        // Not all wallets implement EIP-2255. Local disconnect still works.
      }
    }
    setManualDisconnect(true);
    void track("WALLET_DISCONNECTED", clientContext());
    setProvider(null);
    setAccount(null);
    setWalletName("");
    setWalletTransport("");
    setBalance(null);
    setTransferError("");
    setStep("Ready");
  }

  async function ensureSourceChain() {
    if (!provider) throw new Error("Connect a wallet first");
    void track("CHAIN_SWITCH_REQUESTED", clientContext());
    try {
      await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: `0x${source.viem.id.toString(16)}` }] });
      void track("CHAIN_SWITCHED", clientContext());
    } catch (error) {
      const e = error as { code?: number };
      if (e.code !== 4902) throw error;
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: `0x${source.viem.id.toString(16)}`,
          chainName: source.viem.name,
          nativeCurrency: source.viem.nativeCurrency,
          rpcUrls: [...source.viem.rpcUrls.default.http],
          blockExplorerUrls: source.viem.blockExplorers?.default?.url ? [source.viem.blockExplorers.default.url] : []
        }]
      });
      void track("CHAIN_ADDED", clientContext());
    }
  }

  async function refreshBalance() {
    if (!account) return;
    try {
      // Read from the chain RPC directly. Connecting a wallet must not force a network switch.
      const client = createPublicClient({ chain: source.viem, transport: http(source.viem.rpcUrls.default.http[0]) });
      const value = await client.readContract({ address: source.usdc, abi: erc20Abi, functionName: "balanceOf", args: [account] });
      setBalance(formatUnits(value, 6));
    } catch (error) {
      setBalance(null);
      void captureClientError(error, { stage: "balance", source: source.name });
    }
  }

  async function execute() {
    if (!provider || !account) return;
    if (!isAddress(effectiveRecipient)) {
      setRecipientTouched(true);
      setTransferError("Enter a valid recipient address before transferring.");
      return;
    }
    setBusy(true);
    setFlowStage("creating");
    setTransferError("");
    setMintHash(null);
    setBurnHash(null);

    let activeOperationId: string | null = null;
    try {
      // Refresh the selected fee immediately before signing so stale quotes do not cause a revert.
      const liveQuote = await fetchQuote(finality);
      setQuotes((current) => ({ ...current, [finality]: liveQuote }));
      const liveTotalBurn = BigInt(liveQuote.totalBurn);
      const liveGrandTotal = BigInt(liveQuote.grandTotal);
      const liveBalance = parseUsdcSafe(balance);
      if (liveBalance != null && liveBalance < liveGrandTotal) {
        setTransferError(`Insufficient USDC balance. Need ${liveQuote.display.grandTotal} USDC, balance is ${balance} USDC.`);
        return;
      }

      const createResponse = await fetch("/api/operations", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...clientContext(),
          walletAddress: account,
          recipientAddress: effectiveRecipient,
          sourceDomain: source.domain,
          destinationDomain: destination.domain,
          amount,
          finality,
          gasTopupUsd: selectedGasTopupUsd
        })
      });
      const created = await readApiJson<{ id: string }>(createResponse, "Could not create operation.");
      activeOperationId = created.id;
      setOperationId(activeOperationId);
      saveOperationToHistory(activeOperationId);
      void track("TRANSFER_STARTED", clientContext(), { amount, finality, gasTopupUsd: selectedGasTopupUsd ?? undefined }, activeOperationId);

      await ensureSourceChain();

      if (selectedGasTopupUsd) {
        setStep("Buying destination gas");
        setFlowStage("gas_payment");
        void track("GAS_PURCHASE_STARTED", clientContext(), { gasTopupUsd: selectedGasTopupUsd }, activeOperationId);
        try {
          const gasResult = await payForAndDeliverTestnetGas({
            provider,
            account,
            source,
            destinationDomain: destination.domain,
            operationId: activeOperationId,
            gasTopupUsd: selectedGasTopupUsd,
            onStep: (message) => {
              setStep(message);
              if (/sending|delivered/i.test(message)) setFlowStage("gas_delivery");
              else setFlowStage("gas_payment");
            }
          });
          void track("GAS_DELIVERED", clientContext(), {
            paymentTxHash: gasResult.paymentHash,
            deliveryTxHash: gasResult.delivery.txHash,
            nativeAmount: gasResult.delivery.nativeAmount ?? gasResult.quote.nativeAmount,
            nativeSymbol: gasResult.delivery.nativeSymbol
          }, activeOperationId);
        } catch (gasError) {
          const message = userRejected(gasError) ? "Gas purchase cancelled in wallet." : friendlyError(gasError, "Destination gas step needs attention.");
          setTransferError(message);
          setStep(message);
          void track("GAS_PURCHASE_FAILED", clientContext(), undefined, activeOperationId);
          window.location.assign(`/op/${encodeURIComponent(activeOperationId)}`);
          return;
        }
      }

      const publicClient = createPublicClient({ chain: source.viem, transport: custom(provider) });
      const walletClient = createWalletClient({ chain: source.viem, transport: custom(provider), account });
      const allowance = await publicClient.readContract({
        address: source.usdc,
        abi: erc20Abi,
        functionName: "allowance",
        args: [account, source.tokenMessenger]
      });

      if (allowance < liveTotalBurn) {
        setFlowStage("approval");
        setStep("Approve USDC in wallet");
        void patchOperation(activeOperationId, { status: "APPROVING" });
        void track("APPROVE_REQUESTED", clientContext(), undefined, activeOperationId);
        const approvalHash = await walletClient.writeContract({
          address: source.usdc,
          abi: erc20Abi,
          functionName: "approve",
          args: [source.tokenMessenger, liveTotalBurn],
          account,
          chain: source.viem
        });
        void track("APPROVE_SIGNED", clientContext(), { txHash: approvalHash }, activeOperationId);
        setStep("Waiting for approval confirmation");
        await publicClient.waitForTransactionReceipt({ hash: approvalHash });
        void patchOperation(activeOperationId, { status: "APPROVED" });
        void track("APPROVE_CONFIRMED", clientContext(), { txHash: approvalHash }, activeOperationId);
      }

      setFlowStage("burn_sign");
      setStep("Confirm CCTP burn in wallet");
      void track("TRANSFER_SIGNATURE_REQUESTED", clientContext(), undefined, activeOperationId);
      const hash = await walletClient.writeContract({
        address: source.tokenMessenger,
        abi: tokenMessengerAbi,
        functionName: "depositForBurnWithHook",
        args: [
          liveTotalBurn,
          destination.domain,
          pad(effectiveRecipient as `0x${string}`, { size: 32 }),
          source.usdc,
          ZERO_BYTES32,
          BigInt(liveQuote.maxFee),
          liveQuote.finalityThreshold,
          FORWARDING_HOOK
        ],
        account,
        chain: source.viem
      });
      setBurnHash(hash);
      setFlowStage("burn_submitted");
      await patchOperation(activeOperationId, { status: "BURN_SUBMITTED", sourceTxHash: hash });
      void track("TRANSFER_SIGNED", clientContext(), { txHash: hash }, activeOperationId);
      void track("SOURCE_TX_SUBMITTED", clientContext(), { txHash: hash }, activeOperationId);
      setStep("Burn submitted · waiting for source confirmation");
      localStorage.setItem("scc:lastTransfer", JSON.stringify({
        sourceKey,
        destinationKey,
        sourceDomain: source.domain,
        burnHash: hash,
        operationId: activeOperationId,
        at: Date.now()
      } satisfies SavedTransfer));

      // The operation page owns long-running confirmation/attestation/mint tracking.
      // Move there as soon as the source transaction hash is safely persisted.
      window.location.assign(`/op/${encodeURIComponent(activeOperationId)}`);
      return;
    } catch (error) {
      const rejected = userRejected(error);
      const status = rejected ? "USER_REJECTED" : "FAILED";
      const message = rejected ? "Cancelled in wallet" : friendlyError(error, "Transfer failed. Please retry.");
      setStep(message);
      setTransferError(rejected ? "" : message);
      void patchOperation(activeOperationId, { status });
      void track(status === "USER_REJECTED" ? "USER_REJECTED" : "OPERATION_FAILED", clientContext(), undefined, activeOperationId);
      void captureClientError(error, { stage: "execute", operationId: activeOperationId ?? undefined });
    } finally {
      setBusy(false);
    }
  }

  const amountIsPositive = Number.isFinite(Number(amount)) && Number(amount) > 0;
  const recipientValid = !customRecipient ? Boolean(account) : isAddress(recipient.trim());
  const canTransfer = Boolean(account && quote && amountIsPositive && recipientValid && gasPreviewValid && !insufficientBalance && !busy);
  const selectedQuoteError = quoteErrors[finality];
  const statusTone = mintHash ? "complete" : transferError ? "error" : burnHash || busy ? "pending" : account ? "connected" : "warning";
  const statusText = mintHash ? "Fresh mint complete" : transferError ? transferError : burnHash || busy ? step : account ? "Wallet connected" : "Wallet not connected";
  const progressStages = [
    { key: "creating", label: "Create operation" },
    ...(selectedGasTopupUsd ? [{ key: "gas_payment", label: "Pay destination gas" }, { key: "gas_delivery", label: "Deliver destination gas" }] : []),
    { key: "approval", label: "USDC approval" },
    { key: "burn_sign", label: "CCTP burn" },
    { key: "burn_submitted", label: "Track mint" }
  ] as Array<{ key: TransferStage; label: string }>;
  const progressIndex = Math.max(0, progressStages.findIndex((item) => item.key === flowStage));

  return (
    <main className="page">
      <header className="topbar">
        <div className="brand"><span className="brandMark">FM</span><span>Fresh Mint</span></div>
        <div className="topActions">
          <a className="navLink" href="/history">History</a>
          <button className="themeToggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Toggle color theme" title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}>
            {theme === "dark" ? "☀" : "☾"}
          </button>
          {account ? (
            <button className="walletPill connected" onClick={() => void disconnect()} title={`Disconnect ${walletName || "wallet"}`}>
              <span className="walletDot" />{short(account)}
            </button>
          ) : null}
        </div>
      </header>

      <section className="hero">
        <div className="eyebrow">CCTP TESTNET MVP</div>
        <h1>USDC, minted fresh on arrival.</h1>
        <p>Native Circle CCTP burn → mint. No bridge liquidity pool. No wrapped USDC.</p>
      </section>

      <section className="card">
        <div className="fieldLabel">From</div>
        <div className="rowBox">
          <select value={sourceKey} onChange={(e) => { setSourceKey(e.target.value); setTransferError(""); }}>
            {chains.map((chain) => <option key={chain.key} value={chain.key}>{chain.name}</option>)}
          </select>
          <div className="token">USDC</div>
        </div>

        <div className="amountLine">
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              const next = e.target.value.replace(",", ".");
              if (/^\d*(?:\.\d{0,6})?$/.test(next)) setAmount(next);
            }}
            aria-label="USDC amount"
          />
          <span>USDC</span>
        </div>
        <div className="balanceLine">
          <span className="micro">{account ? `Balance: ${balance ?? "…"} USDC` : "Connect a wallet to see balance"}</span>
          {insufficientBalance && quote ? <span className="inlineError">Insufficient · need {quote.display.grandTotal} USDC</span> : null}
        </div>

        <button className="swapArrow" onClick={() => { const a = sourceKey; setSourceKey(destinationKey); setDestinationKey(a); setTransferError(""); }} aria-label="Swap direction">↓</button>

        <div className="fieldLabel">To</div>
        <div className="rowBox">
          <select value={destinationKey} onChange={(e) => { setDestinationKey(e.target.value); setTransferError(""); }}>
            {chains.filter((chain) => chain.key !== sourceKey).map((chain) => <option key={chain.key} value={chain.key}>{chain.name}</option>)}
          </select>
          <div className="token">USDC</div>
        </div>

        <div className="recipientToggle">
          <label><input type="checkbox" checked={customRecipient} onChange={(e) => { setCustomRecipient(e.target.checked); setRecipientTouched(false); setTransferError(""); }} /> Send to another address</label>
        </div>
        {customRecipient ? (
          <>
            <input
              className={`recipient ${recipientError ? "invalid" : ""}`}
              placeholder="0x recipient address"
              value={recipient}
              onBlur={() => setRecipientTouched(true)}
              onChange={(e) => { setRecipient(e.target.value); setRecipientTouched(true); setTransferError(""); }}
              aria-invalid={Boolean(recipientError)}
            />
            {recipientError ? <div className="fieldError">{recipientError}</div> : recipient && isAddress(recipient.trim()) ? <div className="fieldSuccess">Valid EVM address</div> : null}

            {addressBook.length > 0 ? (
              <div className="recipientSuggestions">
                <span className="suggestionLabel">Saved</span>
                <div className="recipientChips">
                  {addressBook.slice(0, 6).map((entry) => (
                    <button type="button" key={entry.address.toLowerCase()} onClick={() => { setRecipient(entry.address); setRecipientTouched(false); setTransferError(""); }}>
                      <strong>{entry.label}</strong><small>{short(entry.address)}</small>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {recentRecipients.length > 0 ? (
              <div className="recipientSuggestions">
                <span className="suggestionLabel">Recent completed transfers</span>
                <div className="recipientChips">
                  {recentRecipients.slice(0, 5).map((entry) => (
                    <button type="button" key={entry.address.toLowerCase()} onClick={() => { setRecipient(entry.address); setRecipientTouched(false); setTransferError(""); }}>
                      <strong>{short(entry.address)}</strong><small>{entry.destinationChain}</small>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {recipient && isAddress(recipient.trim()) ? (
              <div className="addressBookSave">
                <input placeholder="Label, e.g. Vasya or My exchange" value={addressBookLabel} onChange={(e) => setAddressBookLabel(e.target.value)} />
                <button type="button" onClick={() => {
                  const next = saveAddressBookEntry(recipient.trim(), addressBookLabel);
                  setAddressBook(next);
                  setAddressBookLabel("");
                }}>Save address</button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="recipientReadonly">Recipient: {account ? short(account) : "connected wallet"}</div>
        )}

        <div className="gasPreviewPanel">
          <label className="gasToggle">
            <input type="checkbox" checked={gasTopupEnabled} onChange={(e) => setGasTopupEnabled(e.target.checked)} />
            <span><strong>Need gas on destination</strong><small>Testnet: pay with faucet USDC; FreshMint sends faucet ETH to the recipient before the CCTP burn.</small></span>
          </label>
          {gasTopupEnabled ? (
            <div className="gasChoices">
              {["0.10", "0.50", "5.00"].map((value) => (
                <button type="button" key={value} className={gasTopupUsd === value ? "active" : ""} onClick={() => setGasTopupUsd(value)}>${value}</button>
              ))}
              <button type="button" className={gasTopupUsd === "custom" ? "active" : ""} onClick={() => setGasTopupUsd("custom")}>Custom</button>
              {gasTopupUsd === "custom" ? (
                <input className={`gasCustom ${gasPreviewValid ? "" : "invalid"}`} inputMode="decimal" placeholder="USD" value={customGasTopup} onChange={(e) => setCustomGasTopup(e.target.value.replace(",", "."))} />
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="segmented">
          {(["standard", "fast"] as Finality[]).map((mode) => {
            const modeQuote = quotes[mode];
            const modeError = quoteErrors[mode];
            return (
              <button key={mode} className={finality === mode ? "active" : ""} onClick={() => { setFinality(mode); setTransferError(""); }}>
                <span>{mode === "standard" ? "Standard" : "Fast"}</span>
                <small className="eta">{cctpEta(source.domain, mode)}</small>
                <small>{modeQuote ? `fee ${modeQuote.display.totalFee} USDC` : modeError ? "unavailable" : quotesLoading ? "calculating…" : mode === "standard" ? "hard finality" : "soft finality"}</small>
              </button>
            );
          })}
        </div>

        <div className="quoteBox">
          {quote ? <>
            <div className="receiveRow"><span>You receive on {destination.name}</span><strong>{quote.display.receive} USDC</strong></div>
            <div><span>Destination delivery <span className="infoMark" title="Circle Forwarding Service pays the destination mint transaction in this testnet build.">ⓘ</span></span><span>{quote.display.forwardingFee} USDC</span></div>
            <div><span>CCTP transfer fee</span><span>{quote.display.protocolFee} USDC</span></div>
            {quote.gas ? <>
              <div><span>Destination gas value</span><span>≈${Number(quote.gas.nativeMarketValueUsd).toFixed(2)} → ≈{quote.gas.nativeAmount} {quote.gas.nativeSymbol}</span></div>
              <div><span>Gas service markup</span><span>+${Number(quote.gas.serviceFeeUsd).toFixed(2)}</span></div>
              <div><span>Gas package total</span><span>{quote.display.gasCharge} test USDC</span></div>
            </> : null}
            <div><span>Fresh Mint bridge fee</span><span className="free">0 USDC</span></div>
            <div className="quoteNote"><span>Standard waits for hard finality; Fast uses earlier confirmed finality. The ETA shown above is Circle's typical attestation time, not a guarantee.</span>{quote.gas ? <span>Preset values mean the approximate market value of gas you receive. Destination gas uses a live ETH/USD reference ({quote.gas.priceSource}); the service markup is added on top and shown separately. Delivery network gas is paid by FreshMint and is not deducted from the quoted ETH amount.</span> : null}</div>
            <div className="total"><span>Total from wallet</span><strong>{quote.display.grandTotal} USDC + source gas</strong></div>
          </> : <div className={`quoteLoading ${selectedQuoteError ? "errorText" : ""}`}>{selectedQuoteError || (quotesLoading ? `Calculating ${finality} live fee…` : "Enter an amount to calculate live fees")}</div>}
        </div>

        {operationId && (busy || flowStage !== "idle") ? (
          <div className="liveTransferPanel">
            <div className="liveTransferHeader">
              <div><span className="livePulse" /><strong>Operation in progress</strong></div>
              <a href={`/op/${operationId}`}>Open full status</a>
            </div>
            <div className="liveTransferSteps">
              {progressStages.map((item, index) => (
                <div className={`liveTransferStep ${index < progressIndex ? "complete" : index === progressIndex ? "active" : ""}`} key={item.key}>
                  <span>{index < progressIndex ? "✓" : index + 1}</span><small>{item.label}</small>
                </div>
              ))}
            </div>
            <div className="micro">{step} · Operation {short(operationId)} · auto-saved</div>
          </div>
        ) : null}

        {!account ? (
          <div className="walletConnectArea">
            {sortedWallets.length === 1 && !walletConnectConfigured ? (
              <button className="primary" onClick={() => void connect(sortedWallets[0]!)}>
                {sortedWallets[0]!.icon ? <img src={sortedWallets[0]!.icon} alt="" /> : null}
                Connect {sortedWallets[0]!.name}
              </button>
            ) : (
              <button className="primary" onClick={() => { setWalletModalOpen(true); void track("WALLET_SELECTOR_OPENED", clientContext()); }}>
                Connect wallet
              </button>
            )}
            <div className="micro centered">Previously authorized wallets reconnect silently. First connection always requires an explicit Connect action.</div>
          </div>
        ) : (
          <button className="primary" disabled={!canTransfer} onClick={() => void execute()}>
            {busy ? "Working…" : insufficientBalance ? "Insufficient USDC balance" : `Transfer ${amount || "0"} USDC`}
          </button>
        )}

        <div className={`status ${statusTone}`}>
          <span className="dot"></span>
          <div>
            <strong>{statusText}</strong>
            {account && !burnHash && !transferError ? <span className="micro">{walletName || "Injected wallet"} · {short(account)}</span> : null}
            {operationId ? <span className="micro">Operation {short(operationId)} · <a href={`/op/${operationId}`}>View / resume</a> · <button className="textButton" onClick={() => void navigator.clipboard?.writeText(operationUrl(operationId))}>Copy link</button></span> : null}
            {burnHash ? <a href={`${source.explorer}${burnHash}`} target="_blank" rel="noreferrer">Burn {short(burnHash)}</a> : null}
            {mintHash ? <a href={`${destination.explorer}${mintHash}`} target="_blank" rel="noreferrer">Fresh Mint proof {short(mintHash)}</a> : null}
          </div>
        </div>
      </section>

      {walletModalOpen && !account ? (
        <div className="modalBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setWalletModalOpen(false); }}>
          <section className="walletModal" role="dialog" aria-modal="true" aria-labelledby="wallet-modal-title">
            <div className="modalHeader">
              <div>
                <div className="eyebrow">WALLETS</div>
                <h2 id="wallet-modal-title">Connect wallet</h2>
              </div>
              <button className="modalClose" onClick={() => setWalletModalOpen(false)} aria-label="Close wallet selector">×</button>
            </div>
            <div className="walletGrid">
              {sortedWallets.map((wallet, index) => (
                <button key={wallet.id} className="walletOption" onClick={() => void connect(wallet)}>
                  <span className="walletIcon">{wallet.icon ? <img src={wallet.icon} alt="" /> : <span>{wallet.name.slice(0, 1).toUpperCase()}</span>}</span>
                  <span className="walletOptionText">
                    <strong>{wallet.name}</strong>
                    <small>{index === 0 && preferredWalletId === wallet.id ? "Recently used" : "Detected in browser"}</small>
                  </span>
                </button>
              ))}
            </div>
            <div className="walletDivider"><span>Wallet not listed above?</span></div>
            <button className="walletOption walletConnectOption" disabled={!walletConnectConfigured} onClick={() => void connectViaWalletConnect()}>
              <span className="walletIcon wcIcon">W</span>
              <span className="walletOptionText">
                <strong>WalletConnect</strong>
                <small>{walletConnectConfigured ? "Connect by QR code or supported mobile wallet" : "Needs a Reown project ID in this deployment"}</small>
              </span>
            </button>
            <div className="micro modalHint">Browser wallets above are detected locally. WalletConnect covers compatible wallets not injected into this browser. Fresh Mint never requests seed phrases or private keys.</div>
          </section>
        </div>
      ) : null}

      <footer>
        <span>TESTNET ONLY · tokens have no value</span>
        <span>Circle CCTP V2 · Fresh Mint settlement · v{baseClientContext.clientVersion}</span>
      </footer>
    </main>
  );
}


function ShellHeader({ theme, setTheme }: { theme: Theme; setTheme: (theme: Theme) => void }) {
  return (
    <header className="topbar">
      <a className="brand brandLink" href="/"><span className="brandMark">FM</span><span>Fresh Mint</span></a>
      <div className="topActions">
        <a className="navLink" href="/history">History</a>
        <button className="themeToggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Toggle color theme">
          {theme === "dark" ? "☀" : "☾"}
        </button>
      </div>
    </header>
  );
}

function OperationPage({ id }: { id: string }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [operation, setOperation] = useState<PublicOperation | null>(null);
  const [error, setError] = useState("");
  const [copyText, setCopyText] = useState("Copy link");
  const [gasChoice, setGasChoice] = useState("0.50");
  const [customGas, setCustomGas] = useState("");
  const [savingGas, setSavingGas] = useState(false);
  const [gasWallets, setGasWallets] = useState<DiscoveredWallet[]>([]);
  const [gasProvider, setGasProvider] = useState<Eip1193Provider | null>(null);
  const [gasAccount, setGasAccount] = useState<`0x${string}` | null>(null);
  const [gasWalletName, setGasWalletName] = useState("");
  const [gasActionError, setGasActionError] = useState("");
  const [gasActionStep, setGasActionStep] = useState("");
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [clockNow, setClockNow] = useState(Date.now());

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("scc:theme", theme); } catch { /* noop */ }
  }, [theme]);

  useEffect(() => discoverWallets(setGasWallets), []);

  useEffect(() => {
    if (!operation?.gasTopupUsd) return;
    const normalized = Number(operation.gasTopupUsd).toFixed(2);
    if (["0.10", "0.50", "5.00"].includes(normalized)) setGasChoice(normalized);
    else { setGasChoice("custom"); setCustomGas(normalized); }
  }, [operation?.gasTopupUsd]);

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    saveOperationToHistory(id);
    let stopped = false;
    const refresh = async () => {
      try {
        const current = await fetchOperation(id);
        if (stopped) return;
        setOperation(current);
        setLastRefreshAt(Date.now());
        setError("");

        // CCTP tracking is server-owned. The browser only refreshes the persisted operation state.
      } catch (err) {
        if (!stopped) setError(friendlyError(err, "Could not load operation."));
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 3000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [id]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(operationUrl(id));
      setCopyText("Copied");
      window.setTimeout(() => setCopyText("Copy link"), 1400);
    } catch {
      setCopyText("Copy failed");
    }
  }

  async function connectGasWallet(wallet: DiscoveredWallet) {
    setGasActionError("");
    try {
      const addresses = (await wallet.provider.request({ method: "eth_requestAccounts" })) as string[];
      const address = addresses?.[0];
      if (!address || !isAddress(address)) throw new Error("Wallet returned an invalid address");
      setGasProvider(wallet.provider);
      setGasAccount(address);
      setGasWalletName(wallet.name);
    } catch (err) {
      setGasActionError(userRejected(err) ? "Connection cancelled in wallet." : friendlyError(err, "Could not connect wallet."));
    }
  }

  async function connectGasWalletConnect() {
    setGasActionError("");
    try {
      const wcProvider = await openWalletConnect();
      const addresses = (await wcProvider.request({ method: "eth_accounts" })) as string[];
      const address = addresses?.[0];
      if (!address || !isAddress(address)) throw new Error("WalletConnect returned an invalid address");
      setGasProvider(wcProvider);
      setGasAccount(address);
      setGasWalletName("WalletConnect");
    } catch (err) {
      setGasActionError(userRejected(err) ? "Connection cancelled in wallet." : friendlyError(err, "Could not connect with WalletConnect."));
    }
  }

  async function retryGasDelivery() {
    if (!operation?.gasPaymentTxHash) return;
    setSavingGas(true);
    setGasActionError("");
    setGasActionStep("Retrying destination gas delivery");
    try {
      const response = await fetch(`/api/operations/${encodeURIComponent(id)}/gas/deliver`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ paymentTxHash: operation.gasPaymentTxHash })
      });
      const result = await readApiJson<{ txHash: string; nativeAmount?: string | null; nativeSymbol?: string }>(response, "Could not retry destination gas delivery.");
      setGasActionStep(`Delivered ${result.nativeAmount ?? ""} ${result.nativeSymbol ?? "ETH"}`.trim());
      setOperation(await fetchOperation(id));
    } catch (err) {
      setGasActionError(friendlyError(err, "Could not retry destination gas delivery."));
    } finally {
      setSavingGas(false);
    }
  }

  async function buyAndSendGas() {
    if (!operation || !source || !gasProvider || !gasAccount) {
      setGasActionError("Connect the source EVM wallet to pay for the test gas package.");
      return;
    }
    if (operation.walletAddress && gasAccount.toLowerCase() !== operation.walletAddress.toLowerCase()) {
      setGasActionError(`Connect the original source account ${short(operation.walletAddress)} to pay for this gas package.`);
      return;
    }
    const value = gasChoice === "custom" ? customGas.trim() : gasChoice;
    if (!value || Number(value) < 0.01 || Number(value) > 5) {
      setGasActionError("Choose a gas package between $0.01 and $5.00.");
      return;
    }
    setSavingGas(true);
    setGasActionError("");
    setGasActionStep("Preparing gas package");
    try {
      await patchOperation(id, { gasTopupUsd: value, gasStatus: "REQUIRED" });
      const result = await payForAndDeliverTestnetGas({
        provider: gasProvider,
        account: gasAccount,
        source,
        destinationDomain: operation.destinationDomain,
        operationId: id,
        gasTopupUsd: value,
        onStep: setGasActionStep
      });
      setGasActionStep(`Delivered ${result.delivery.nativeAmount ?? result.quote.nativeAmount} ${result.delivery.nativeSymbol}`);
      setOperation(await fetchOperation(id));
    } catch (err) {
      setGasActionError(userRejected(err) ? "Gas purchase cancelled in wallet." : friendlyError(err, "Could not deliver destination gas."));
    } finally {
      setSavingGas(false);
    }
  }


  const source = operation ? chainByDomain(operation.sourceDomain) : null;
  const destination = operation ? chainByDomain(operation.destinationDomain) : null;
  const activeTimelineStep = operation?.steps.find((item) => item.status === "active") ?? null;
  const waitingForAttestation = Boolean(operation && ["BURN_SUBMITTED", "SOURCE_CONFIRMED"].includes(operation.status));
  const waitingForMint = operation?.status === "ATTESTED";
  const activeSince = activeTimelineStep?.updatedAt ? new Date(activeTimelineStep.updatedAt).getTime() : operation ? new Date(operation.updatedAt).getTime() : clockNow;
  const elapsedSeconds = Math.max(0, Math.floor((clockNow - activeSince) / 1000));
  const formatElapsed = (seconds: number) => `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;
  const expectedUpperSeconds = operation ? (operation.finality === "standard" ? 19 * 60 : operation.sourceDomain === 0 ? 20 : 8) : 1;
  const estimatedWaitProgress = waitingForAttestation ? Math.min(95, Math.max(2, (elapsedSeconds / expectedUpperSeconds) * 100)) : 0;
  const progressHeadline = waitingForAttestation
    ? "Waiting for Circle attestation"
    : waitingForMint
      ? "Attestation received · waiting for destination mint"
      : activeTimelineStep
        ? activeTimelineStep.label
        : operation?.status === "COMPLETED" ? "Settlement complete" : "Operation is updating";
  const progressDetail = waitingForAttestation && operation
    ? `Elapsed ${formatElapsed(elapsedSeconds)} · typical ${cctpEta(operation.sourceDomain, operation.finality)}`
    : waitingForMint
      ? `Elapsed ${formatElapsed(elapsedSeconds)} · Circle Forwarding Service is submitting the destination transaction`
      : operation?.status === "COMPLETED"
        ? "All settlement steps are confirmed"
        : `Elapsed ${formatElapsed(elapsedSeconds)}`;
  const blockingText = !operation ? "" : operation.status === "DESTINATION_WALLET_REQUIRED"
    ? `Connect a ${operation.requiredWalletFamily || "destination"} wallet to continue.`
    : operation.gasPaymentTxHash && ["PAYMENT_SUBMITTED", "VERIFYING", "PURCHASED", "SENDING"].includes(operation.gasStatus ?? "")
      ? "The gas payment is recorded. Verification or delivery can be retried without paying again."
      : operation.status === "GAS_REQUIRED"
        ? "Destination gas is required before the transfer can continue."
        : operation.status === "USER_REJECTED"
          ? "The previous wallet request was cancelled. Resume when ready."
          : operation.status === "FAILED"
            ? "The operation stopped on an error. Completed on-chain steps remain recoverable below."
            : operation.status === "COMPLETED"
              ? "Fresh Mint settlement is complete."
              : operation.sourceTxHash
                ? "Tracking continues automatically. You can close this tab and return with this link."
                : "This operation has not submitted a source transaction yet.";

  return (
    <main className="page">
      <ShellHeader theme={theme} setTheme={setTheme} />
      <section className="detailHero">
        <div className="eyebrow">OPERATION</div>
        <h1>Transfer status</h1>
        <p>{short(id)}</p>
      </section>

      <section className="card detailCard">
        {error ? <div className="errorPanel">{error}</div> : !operation ? <div className="loadingPanel">Loading operation…</div> : <>
          <div className="operationSummary">
            <div><span>Route</span><strong>{operation.sourceChain} → {operation.destinationChain}</strong></div>
            <div><span>Amount</span><strong>{operation.amount} USDC</strong></div>
            <div><span>Mode</span><strong>{operation.finality === "fast" ? "Fast" : "Standard"} · {cctpEta(operation.sourceDomain, operation.finality)}</strong></div>
            <div><span>Status</span><strong className={`statusLabel ${operation.status.toLowerCase()}`}>{operation.status.replaceAll("_", " ")}</strong></div>
          </div>

          <div className={`operationLiveBar ${operation.status === "COMPLETED" ? "complete" : ""}`}>
            <span className="livePulse" />
            <strong>{operation.status === "COMPLETED" ? "Final" : "Live status"}</strong>
            <span>{operation.status === "COMPLETED" ? "Settlement complete" : `Auto-updates every 3 sec${lastRefreshAt ? ` · last ${new Date(lastRefreshAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}`}</span>
          </div>

          {operation.status !== "COMPLETED" ? (
            <div className="operationProgressCard">
              <div className="operationProgressTitle"><span className="spinner" /><div><strong>{progressHeadline}</strong><span>{progressDetail}</span></div></div>
              <div className={`operationProgressTrack ${waitingForAttestation ? "estimated" : "indeterminate"}`} aria-label="Operation progress">
                <span style={waitingForAttestation ? { width: `${estimatedWaitProgress}%` } : undefined} />
              </div>
              <small>{waitingForAttestation ? "Time bar is an estimate, not block-by-block completion." : "This status updates automatically; no refresh is required."}</small>
            </div>
          ) : null}

          {operation.errorMessage ? (
            <div className="operationErrorDetail">
              <strong>{operation.errorCode ?? "Operation needs attention"}</strong>
              <span>{operation.errorMessage}</span>
              {operation.gasPaymentTxHash ? <small>Your gas-payment transaction is saved; retry will not request a second payment.</small> : null}
            </div>
          ) : null}

          <div className="operationActions">
            <button className="secondary" onClick={() => void copyLink()}>{copyText}</button>
            <a className="secondary buttonLink" href="/">New transfer</a>
          </div>

          <div className="resumePanel"><strong>Resume</strong><span>{blockingText}</span></div>

          <div className="timeline">
            {operation.steps.map((item) => (
              <div className={`timelineItem ${item.status}`} key={item.key}>
                <span className="timelineDot" />
                <div>
                  <strong>{item.label}</strong>
                  <span>{item.chain || ""}{item.walletFamily && item.walletFamily !== "SYSTEM" ? ` · ${item.walletFamily} wallet` : ""}</span>
                  {item.txHash && item.key === "source" && source ? <a href={`${source.explorer}${item.txHash}`} target="_blank" rel="noreferrer">Source tx {short(item.txHash)}</a> : null}
                  {item.txHash && item.key === "gas" && source && destination ? (
                    <a href={`${operation.gasDeliveryTxHash && item.txHash.toLowerCase() === operation.gasDeliveryTxHash.toLowerCase() ? destination.explorer : source.explorer}${item.txHash}`} target="_blank" rel="noreferrer">
                      {operation.gasDeliveryTxHash && item.txHash.toLowerCase() === operation.gasDeliveryTxHash.toLowerCase() ? "Gas delivery" : "Gas payment"} {short(item.txHash)}
                    </a>
                  ) : null}
                  {item.txHash && item.key === "destination" && destination ? <a href={`${destination.explorer}${item.txHash}`} target="_blank" rel="noreferrer">Destination tx {short(item.txHash)}</a> : null}
                </div>
              </div>
            ))}
          </div>

          <div className="gasOperationPanel">
            <div>
              <strong>Destination gas</strong>
              <span>{operation.gasStatus === "DELIVERED" ? `${operation.gasNativeAmount ?? ""} ${operation.gasNativeSymbol ?? "ETH"} delivered` : operation.gasTopupUsd ? `Selected gas value: $${Number(operation.gasTopupUsd).toFixed(2)}` : "No gas top-up selected"}</span>
              {operation.gasTopupUsd && operation.gasMarkupBps != null ? <small>Gas value ${Number(operation.gasTopupUsd).toFixed(2)} + service markup {(operation.gasMarkupBps / 100).toFixed(2)}% = ≈${(Number(operation.gasTopupUsd) * (1 + operation.gasMarkupBps / 10_000)).toFixed(2)} test USDC charged.</small> : null}
              <small>You can buy test destination gas even after the bridge operation has started. Payment is test USDC on the original source chain; FreshMint sends faucet ETH to the recipient. Delivery transaction gas is paid separately by FreshMint.</small>
              {operation.gasPaymentTxHash && source ? <a href={`${source.explorer}${operation.gasPaymentTxHash}`} target="_blank" rel="noreferrer">Gas payment {short(operation.gasPaymentTxHash)}</a> : null}
            </div>
            {operation.gasStatus !== "DELIVERED" ? <>
              <div className="gasChoices">
                {["0.10", "0.50", "5.00"].map((value) => <button key={value} className={gasChoice === value ? "active" : ""} onClick={() => setGasChoice(value)}>${value}</button>)}
                <button className={gasChoice === "custom" ? "active" : ""} onClick={() => setGasChoice("custom")}>Custom</button>
                {gasChoice === "custom" ? <input className="gasCustom" inputMode="decimal" placeholder="USD" value={customGas} onChange={(e) => setCustomGas(e.target.value.replace(",", "."))} /> : null}
              </div>
              {operation.gasPaymentTxHash && ["PAYMENT_SUBMITTED", "VERIFYING", "PURCHASED", "SENDING"].includes(operation.gasStatus ?? "") ? (
                <div className="gasWalletChoices">
                  <span className="micro">Gas-payment transaction is saved. Retry verification/delivery without paying again.</span>
                  <button className="secondary" disabled={savingGas} onClick={() => void retryGasDelivery()}>{savingGas ? "Retrying…" : "Retry gas delivery"}</button>
                </div>
              ) : <>
                {!gasAccount ? (
                  <div className="gasWalletChoices">
                    <span className="micro">Connect the source EVM wallet that will pay test USDC:</span>
                    {gasWallets.map((wallet) => <button className="secondary" key={wallet.id} onClick={() => void connectGasWallet(wallet)}>{wallet.icon ? <img src={wallet.icon} alt="" /> : null}{wallet.name}</button>)}
                    {walletConnectConfigured ? <button className="secondary" onClick={() => void connectGasWalletConnect()}>WalletConnect</button> : null}
                  </div>
                ) : <div className="micro">Paying wallet: {gasWalletName} · {short(gasAccount)}{operation.walletAddress && gasAccount.toLowerCase() !== operation.walletAddress.toLowerCase() ? " · wrong source account" : ""}</div>}
                <button className="secondary" disabled={savingGas || !gasAccount || Boolean(operation.walletAddress && gasAccount.toLowerCase() !== operation.walletAddress.toLowerCase())} onClick={() => void buyAndSendGas()}>{savingGas ? "Working…" : "Buy & send test gas"}</button>
              </>}
              {gasActionStep ? <div className="micro gasProgress">{gasActionStep}</div> : null}
              {gasActionError ? <div className="fieldError">{gasActionError}</div> : null}
            </> : operation.gasDeliveryTxHash && destination ? <a href={`${destination.explorer}${operation.gasDeliveryTxHash}`} target="_blank" rel="noreferrer">View gas delivery transaction</a> : null}
          </div>
        </>}
      </section>
      <footer><span>TESTNET ONLY · tokens have no value</span><span>Operation links are view/resume links, not signing authority · v{baseClientContext.clientVersion}</span></footer>
    </main>
  );
}

function HistoryPage() {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [operations, setOperations] = useState<PublicOperation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("scc:theme", theme); } catch { /* noop */ }
  }, [theme]);

  useEffect(() => {
    const ids = readOperationHistory();
    Promise.allSettled(ids.map((id) => fetchOperation(id))).then((results) => {
      const loaded: PublicOperation[] = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") loaded.push(result.value);
        else if (ids[index]) removeOperationFromHistory(ids[index]!);
      });
      loaded.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
      setOperations(loaded);
      setLoading(false);
    });
  }, []);

  return (
    <main className="page">
      <ShellHeader theme={theme} setTheme={setTheme} />
      <section className="detailHero">
        <div className="eyebrow">LOCAL HISTORY</div>
        <h1>Your operations</h1>
        <p>No account required. This browser stores only operation IDs; operation state lives on the Fresh Mint backend.</p>
      </section>
      <section className="historyList">
        {loading ? <div className="card loadingPanel">Loading history…</div> : operations.length === 0 ? (
          <div className="card emptyState"><strong>No operations in this browser yet.</strong><a href="/">Start a transfer</a></div>
        ) : operations.map((operation) => (
          <a className="historyCard" href={`/op/${operation.id}`} key={operation.id}>
            <div><strong>{operation.amount} USDC</strong><span>{operation.sourceChain} → {operation.destinationChain}</span></div>
            <div className="historyRight"><strong>{operation.status.replaceAll("_", " ")}</strong><span>{new Date(operation.createdAt).toLocaleString()}</span></div>
          </a>
        ))}
      </section>
      <footer><span>History is local to this browser until wallet accounts are introduced.</span><span>Fresh Mint · v{baseClientContext.clientVersion}</span></footer>
    </main>
  );
}

function Router() {
  const path = window.location.pathname;
  if (path === "/design" || path === "/design/") return <DesignPrototypePage />;
  const match = path.match(/^\/op\/(scc_[0-9a-f-]{36})\/?$/i);
  if (match?.[1]) return <OperationPage id={match[1]} />;
  if (path === "/history" || path === "/history/") return <HistoryPage />;
  return <App />;
}

createRoot(document.getElementById("root")!).render(<React.StrictMode><Router /></React.StrictMode>);
